import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Search, User } from 'lucide-react';
import { api } from '../services/api';
import type { Department, Contact } from '../types';
import { FeedbackModal } from './FeedbackModal';

interface StartConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Template {
  name: string;
  status: string;
  category?: string;
  language?: string;
}

export const StartConversationModal: React.FC<StartConversationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<any>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const contactSearchRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    phoneNumber: '',
    templateName: '',
    departmentId: '',
    variables: [''] as string[],
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadDepartments();
    }
  }, [isOpen]);

  // Search contacts with debounce
  useEffect(() => {
    if (!contactSearch.trim()) {
      setContacts([]);
      setShowContactSuggestions(false);
      return;
    }

    const debounceTimer = setTimeout(() => {
      searchContacts(contactSearch);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [contactSearch]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(event.target as Node)) {
        setShowContactSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const data = await api.getTemplates();
      // Only show approved templates
      setTemplates(data.data.filter((t: Template) => t.status === 'APPROVED'));
    } catch (error) {
      console.error('Failed to load templates:', error);
      setError('Falha ao carregar templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await api.getDepartments();
      setDepartments(data.filter((d: Department) => d.isActive));
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const searchContacts = async (searchTerm: string) => {
    try {
      setLoadingContacts(true);
      const response = await api.getContacts({ search: searchTerm, limit: '10' });
      setContacts(response.data.contacts);
      setShowContactSuggestions(true);
    } catch (error) {
      console.error('Failed to search contacts:', error);
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    setContactSearch(contact.name);
    setFormData({ ...formData, phoneNumber: contact.phone });
    setShowContactSuggestions(false);
  };

  const formatPhone = (phone: string) => {
    if (phone.length === 13 && phone.startsWith('55')) {
      const areaCode = phone.substring(2, 4);
      const firstPart = phone.substring(4, 9);
      const secondPart = phone.substring(9, 13);
      return `+55 (${areaCode}) ${firstPart}-${secondPart}`;
    }
    return phone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Build components array for template variables
      const components = [];
      if (formData.variables.some(v => v.trim())) {
        components.push({
          type: 'body',
          parameters: formData.variables
            .filter(v => v.trim())
            .map(v => ({
              type: 'text',
              text: v,
            })),
        });
      }

      await api.startConversationWithTemplate({
        phoneNumber: formData.phoneNumber,
        templateName: formData.templateName,
        components: components.length > 0 ? components : undefined,
        departmentId: formData.departmentId || undefined,
      });

      setFeedbackType('success');
      setFeedbackMessage('Conversa iniciada com sucesso! A mensagem de template foi enviada.');
      setShowFeedback(true);
      onSuccess();
      resetForm();
    } catch (error: any) {
      setFeedbackType('error');
      setFeedbackMessage(error.response?.data?.error || 'Falha ao iniciar conversa');
      setShowFeedback(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedback(false);
    if (feedbackType === 'success') {
      onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      phoneNumber: '',
      templateName: '',
      departmentId: '',
      variables: [''],
    });
    setSelectedTemplateDetails(null);
    setError(null);
    setContactSearch('');
    setContacts([]);
    setSelectedContact(null);
    setShowContactSuggestions(false);
  };

  const handleTemplateChange = async (templateName: string) => {
    setFormData({ ...formData, templateName });
    setSelectedTemplateDetails(null);

    if (!templateName) return;

    // Try to fetch template details
    try {
      setLoadingTemplateDetails(true);
      const details = await api.getTemplateDetails(templateName);
      setSelectedTemplateDetails(details);
    } catch (error) {
      console.log('Template details not available from API');
      setSelectedTemplateDetails(null);
    } finally {
      setLoadingTemplateDetails(false);
    }
  };

  const getTemplateBodyText = () => {
    if (!selectedTemplateDetails) return null;

    // Try to extract body text from template components
    const bodyComponent = selectedTemplateDetails.components?.find((c: any) => c.type === 'BODY');
    return bodyComponent?.text || null;
  };

  const renderTemplatePreview = () => {
    const bodyText = getTemplateBodyText();

    if (loadingTemplateDetails) {
      return (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">Carregando preview do template...</p>
        </div>
      );
    }

    if (!bodyText && formData.templateName) {
      return (
        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Template selecionado:</strong> {formData.templateName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Preview não disponível. Preencha as variáveis abaixo se o template as utilizar.
          </p>
        </div>
      );
    }

    if (bodyText) {
      // Replace variables with placeholders or actual values
      let previewText = bodyText;
      formData.variables.forEach((value, index) => {
        const variable = `{{${index + 1}}}`;
        if (previewText.includes(variable)) {
          previewText = previewText.replace(
            new RegExp(variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            value || `[Variável ${index + 1}]`
          );
        }
      });

      return (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Preview da Mensagem:</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{previewText}</p>
        </div>
      );
    }

    return null;
  };

  // Force preview update when variables change
  useEffect(() => {
    setPreviewKey(prev => prev + 1);
  }, [formData.variables]);

  const addVariable = () => {
    setFormData({
      ...formData,
      variables: [...formData.variables, ''],
    });
  };

  const removeVariable = (index: number) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter((_, i) => i !== index),
    });
  };

  const updateVariable = (index: number, value: string) => {
    const newVariables = [...formData.variables];
    newVariables[index] = value;
    setFormData({ ...formData, variables: newVariables });
  };

  if (!isOpen) return null;

  return (
    <>
      <FeedbackModal
        isOpen={showFeedback}
        onClose={handleFeedbackClose}
        type={feedbackType}
        title={feedbackType === 'success' ? 'Sucesso!' : 'Erro'}
        message={feedbackMessage}
      />
      <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-lg z-50">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Iniciar Conversa com Template
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contact Search Field */}
              <div className="relative" ref={contactSearchRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Buscar Contato Salvo (Opcional)
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => {
                      setContactSearch(e.target.value);
                      setSelectedContact(null);
                    }}
                    placeholder="Digite o nome ou número do contato..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  {loadingContacts && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>

                {/* Contact Suggestions Dropdown */}
                {showContactSuggestions && contacts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {contacts.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleContactSelect(contact)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0 flex items-center space-x-3 transition"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {contact.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatPhone(contact.phone)}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {showContactSuggestions && contactSearch && contacts.length === 0 && !loadingContacts && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                      Nenhum contato encontrado
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Pesquise por nome ou número de telefone
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Número do WhatsApp *
                </label>
                <input
                  type="text"
                  required
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="5534999999999"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {selectedContact
                    ? `Número do contato selecionado: ${selectedContact.name}`
                    : 'Digite o número com código do país e DDD, sem espaços ou símbolos'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template *
                </label>
                {loadingTemplates ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">Carregando templates...</div>
                ) : templates.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Nenhum template aprovado disponível. Crie um template primeiro.
                  </div>
                ) : (
                  <>
                    <select
                      required
                      value={formData.templateName}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    >
                      <option value="">Selecione um template</option>
                      {templates.map((template) => (
                        <option key={template.name} value={template.name}>
                          {template.name} {template.category && `(${template.category})`}
                        </option>
                      ))}
                    </select>
                    <div key={previewKey}>{renderTemplatePreview()}</div>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Departamento (Opcional)
                </label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="">Sem departamento</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Variáveis do Template (Opcional)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Se o template usa variáveis como {"{{1}}"}, {"{{2}}"}, preencha os valores abaixo
                </p>
                {formData.variables.map((variable, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={variable}
                      onChange={(e) => updateVariable(index, e.target.value)}
                      placeholder={`Valor para {{${index + 1}}}`}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                    {formData.variables.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVariable(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addVariable}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  + Adicionar variável
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading || templates.length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{isLoading ? 'Enviando...' : 'Iniciar Conversa'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
